import { createSlice } from "@reduxjs/toolkit";
import { accountsApi } from "../services/user";
import { tfaApi } from "../services/tfa";
import { authApi } from "../services/auth";
import type { RootState } from "..";
import {
  AccountType,
  PhoneChannel,
  User,
  KycProvider,
} from "../../types/UserTypes";

const initialState: User = {
  id: "",
  account_type: AccountType.CONTRACTOR,
  email: "",
  phone: "",
  phone_channel: PhoneChannel.SMS,
  phone_country: "",
  first_name: "",
  last_name: "",
  invoicing_data: {
    company_name: "",
    address: "",
    country: "",
    city: "",
    reg_number: "",
    tax_id: "",
    postal_code: "",
  },
  marketing_data: {
    notifications_enabled: false,
  },
  is_2fa_enabled: false,
  kyc_country: "",
  kyc_complete: false,
  kyc_complete_and_approved: false,
  kyc_second_level_complete: false,
  kyc_second_level_complete_and_approved: false,
  kyc_second_level_count_threshold: 0,
  kyc_second_level_amount_threshold: 0,
  requires_first_level_kyc: false,
  kyc_first_level_amount_threshold: 0,
  allowed_incoming_currencies: [],
  allowed_outgoing_currencies: [],
  allowed_outgoing_chains: undefined,
  outgoing_currencies_for_incoming_currency: undefined,
  is_onmeta_eligible: false,
  onmeta_kyc_required: false,
  onmeta_kyc_complete: false,
  onmeta_kyc_complete_and_approved: false,
  is_usd_eligible: false,
  usd_kyc_complete: false,
  usd_kyc_complete_and_approved: false,
  usd_tos_accepted: false,
  primary_kyc_provider: KycProvider.SUMSUB,
};

const userDataUpdate = (state: User, { payload }: any) => {
  state.id = payload.id;
  state.account_type = payload.account_type;
  state.first_name = payload.first_name;
  state.last_name = payload.last_name;
  state.email = payload.email;
  state.phone = payload.phone || initialState.phone;
  state.phone_channel = payload.phone_channel || initialState.phone_channel;
  state.phone_country = payload.phone_country || initialState.phone_country;
  state.is_2fa_enabled = payload.is_2fa_enabled || initialState.is_2fa_enabled;
  state.kyc_complete = payload.kyc_complete || initialState.kyc_complete;

  state.kyc_complete_and_approved =
    payload.kyc_complete_and_approved || initialState.kyc_complete_and_approved;

  state.kyc_second_level_complete =
    payload.kyc_second_level_complete || initialState.kyc_second_level_complete;

  state.kyc_second_level_complete_and_approved =
    payload.kyc_second_level_complete_and_approved ||
    initialState.kyc_second_level_complete_and_approved;

  state.kyc_second_level_count_threshold =
    payload.kyc_second_level_count_threshold ||
    initialState.kyc_second_level_count_threshold;

  state.kyc_second_level_amount_threshold =
    payload.kyc_second_level_amount_threshold ||
    initialState.kyc_second_level_amount_threshold;

  state.requires_first_level_kyc =
    payload.requires_first_level_kyc || initialState.requires_first_level_kyc;

  state.kyc_first_level_amount_threshold =
    payload.kyc_first_level_amount_threshold ||
    initialState.kyc_first_level_amount_threshold;

  state.invoicing_data = payload.invoicing_data || initialState.invoicing_data;
  state.marketing_data = payload.marketing_data || initialState.marketing_data;

  state.allowed_incoming_currencies = payload.allowed_incoming_currencies || [];
  state.allowed_outgoing_currencies = payload.allowed_outgoing_currencies || [];
  state.allowed_outgoing_chains = payload.allowed_outgoing_chains || [];

  state.outgoing_currencies_for_incoming_currency =
    payload.outgoing_currencies_for_incoming_currency;

  state.kyc_country = payload.kyc_country || initialState.kyc_country;

  state.is_onmeta_eligible =
    payload.is_onmeta_eligible || initialState.is_onmeta_eligible;

  state.onmeta_kyc_required =
    payload.onmeta_kyc_required || initialState.onmeta_kyc_required;

  state.onmeta_kyc_complete =
    payload.onmeta_kyc_complete || initialState.onmeta_kyc_complete;

  state.onmeta_kyc_complete_and_approved =
    payload.onmeta_kyc_complete_and_approved ||
    initialState.onmeta_kyc_complete_and_approved;

  state.is_usd_eligible =
    payload.is_usd_eligible || initialState.is_usd_eligible;

  state.usd_kyc_complete =
    payload.usd_kyc_complete || initialState.usd_kyc_complete;

  state.usd_tos_accepted =
    payload.usd_tos_accepted || initialState.usd_tos_accepted;

  state.usd_kyc_complete_and_approved =
    payload.usd_kyc_complete_and_approved ||
    initialState.usd_kyc_complete_and_approved;

  state.primary_kyc_provider =
    payload.primary_kyc_provider || initialState.primary_kyc_provider;
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateEmail: (state, action) => {
      state.email = action.payload;
    },
    updatePhoneState: (state, action) => {
      state.phone = action.payload;
    },
    updateKyc: (state, action) => {
      state.kyc_complete = action.payload.kyc_complete;
      state.kyc_complete_and_approved =
        action.payload.kyc_complete_and_approved;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.signup.matchFulfilled, userDataUpdate)
      .addMatcher(accountsApi.endpoints.getUser.matchFulfilled, userDataUpdate)
      .addMatcher(
        accountsApi.endpoints.changeDefaultInvoiceData.matchFulfilled,
        userDataUpdate
      )
      .addMatcher(
        accountsApi.endpoints.changeMarketingNotification.matchFulfilled,
        userDataUpdate
      )
      .addMatcher(tfaApi.endpoints.enableTfa.matchFulfilled, (state) => {
        state.is_2fa_enabled = true;
      })
      .addMatcher(tfaApi.endpoints.disableTfa.matchFulfilled, (state) => {
        state.is_2fa_enabled = false;
      });
  },
});

export const { updateEmail, updateKyc, updatePhoneState } = userSlice.actions;

export default userSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.user;
